import React, { useState, useEffect } from 'react';
import './FAQ.css';

const FAQ_DATA = [
  {
    question: "How does the college chances calculator work?",
    answer: "Our college chances calculator analyzes your academic profile including GPA, SAT/ACT scores, and intended major to match you with schools where you're most likely to be admitted. We categorize schools into Safety (high likelihood of admission), Target (moderate likelihood), and Reach (competitive but possible) based on how your stats compare to each school's admitted student profile."
  },
  {
    question: "What GPA and test scores do I need for top colleges?",
    answer: "Top-tier colleges like Ivy League schools typically look for unweighted GPAs of 3.9+ and SAT scores above 1500 or ACT scores of 34+. However, admissions is holistic—extracurriculars, essays, and recommendations also matter significantly. Our tool helps you find schools that match your current profile while identifying reach schools worth pursuing."
  },
  {
    question: "Should I take the SAT or ACT?",
    answer: "Both tests are accepted equally by colleges. The SAT focuses more on evidence-based reading and math reasoning, while the ACT includes a science section and tends to be more straightforward. We recommend taking a practice test of each to see which format suits you better. You can enter scores from either test (or both) in our calculator."
  },
  {
    question: "What's the difference between Safety, Target, and Reach schools?",
    answer: "Safety schools are where your academic stats exceed the typical admitted student—you have a high chance of admission. Target schools match your profile closely, giving you a reasonable chance. Reach schools are more competitive for your stats but still possible. A balanced college list should include 2-3 schools from each category."
  },
  {
    question: "How many colleges should I apply to?",
    answer: "Most college counselors recommend applying to 8-12 schools: 2-3 safety schools, 4-5 target schools, and 2-3 reach schools. This gives you options while keeping the application process manageable. Our tool helps you build a balanced list based on your specific academic profile."
  },
  {
    question: "When should I start preparing for college admissions?",
    answer: "Ideally, start thinking about college in 9th or 10th grade by focusing on your GPA and exploring extracurriculars. Take the PSAT in 10th/11th grade for practice. Most students take the SAT or ACT in spring of junior year, leaving fall of senior year for applications. Our tool works for students from middle school through senior year."
  },
  {
    question: "Does your calculator consider factors beyond grades and test scores?",
    answer: "Our initial matching is based on academic metrics (GPA, SAT/ACT scores) which are the most quantifiable factors in admissions. After generating your report, you can use our AI counselor to discuss other factors like extracurriculars, intended major competitiveness, geographic preferences, and financial considerations to refine your list."
  },
  {
    question: "Is this college admissions tool free to use?",
    answer: "Yes, our My College Chances tool is completely free. You'll get a personalized report with Safety, Target, and Reach school recommendations based on your academic profile. You can also chat with our AI counselor to refine your college list based on your preferences."
  }
];

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Inject FAQ schema for SEO
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_DATA.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.textContent = JSON.stringify(schema);

    // Remove existing schema if present
    const existing = document.getElementById('faq-schema');
    if (existing) {
      existing.remove();
    }

    document.head.appendChild(script);

    return () => {
      const schemaScript = document.getElementById('faq-schema');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  return (
    <section className="faq-section" aria-labelledby="faq-heading">
      <div className="faq-container">
        <h2 id="faq-heading" className="faq-title">
          Frequently Asked Questions About College Admissions
        </h2>
        <p className="faq-subtitle">
          Get answers to common questions about college chances, SAT/ACT scores, and building your college list.
        </p>

        <div className="faq-list">
          {FAQ_DATA.map((item, index) => (
            <div
              key={index}
              className={`faq-item ${openIndex === index ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <span>{item.question}</span>
                <span className="faq-icon" aria-hidden="true">
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              <div
                id={`faq-answer-${index}`}
                className="faq-answer"
                role="region"
                aria-labelledby={`faq-question-${index}`}
                hidden={openIndex !== index}
              >
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FAQ;
